import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import CryptoJS from 'crypto-js'; // Importar la biblioteca crypto-js
import BASE_URL from '../config/config';
import { FiUser, FiLock, FiArrowLeft, FiEye, FiEyeOff } from 'react-icons/fi';
import { consumirMotivoCaducidad, EVENTO_SESION_CADUCADA, obtenerDispositivo, guardarDispositivo } from '../auth/sesion';
import SelectorIdioma from '../components/ui/SelectorIdioma';
import CodigoVerificacion from '../components/ui/CodigoVerificacion';

const IniciarSesion = () => {
  const { t } = useTranslation();
  const [usuario, setUsuario] = useState('');
  const [contrasena, setContrasena] = useState('');
  const [error, setError] = useState('');
  const [mostrarContrasena, setMostrarContrasena] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [codigoMfa, setCodigoMfa] = useState('');
  const [enviando, setEnviando] = useState(false);
  // Lo decide la cuenta (perfil): si permite recordar navegadores, se ofrece aquí
  const [puedeRecordar, setPuedeRecordar] = useState(false);
  const [diasConfianza, setDiasConfianza] = useState(90);
  const [confiar, setConfiar] = useState(true);
  // La marca la deja el cierre automático de sesión y se consume una sola vez.
  const [sesionCaducada, setSesionCaducada] = useState(consumirMotivoCaducidad);

  const navigate = useNavigate();
  const location = useLocation();
  const successMessage = location.state?.successMessage;

  // Si la sesión caduca estando ya en esta pantalla, no hay recarga: avisamos por evento.
  useEffect(() => {
    const alCaducar = () => setSesionCaducada(true);
    window.addEventListener(EVENTO_SESION_CADUCADA, alCaducar);
    return () => window.removeEventListener(EVENTO_SESION_CADUCADA, alCaducar);
  }, []);

  const handleUsuarioChange = (event) => {
    // Mantener el valor tal cual (no convertir a minúsculas para emails)
    setUsuario(event.target.value);
  };

  const handleContrasenaChange = (event) => {
    setContrasena(event.target.value);
  };

  // El paso MFA recibe el código directamente cuando lo lanza el autoenvío
  // al completar las casillas, porque el estado aún no se ha actualizado.
  const handleSubmit = async (codigo = codigoMfa) => {
    if (enviando) return;
    setSesionCaducada(false);
    setEnviando(true);
    try {
      // Encriptar la contraseña en MD5 antes de enviarla
      const contrasenaEncriptada = CryptoJS.MD5(contrasena).toString();

      const response = mfaToken
        ? await axios.post(`${BASE_URL}/mfa/verificar`, { mfa_token: mfaToken, codigo, dispositivo: obtenerDispositivo(), confiar: puedeRecordar && confiar })
        : await axios.post(`${BASE_URL}/login`, { usuario: usuario.toLowerCase(), contrasena: contrasenaEncriptada, dispositivo: obtenerDispositivo() });
      
      if (response.status === 200) {
        if (response.data.mfa_requerido) {
          setMfaToken(response.data.mfa_token);
          setPuedeRecordar(Boolean(response.data.puede_recordar));
          if (response.data.dias_confianza) setDiasConfianza(response.data.dias_confianza);
          setCodigoMfa('');
          setError('');
          return;
        }
        // Iniciar sesión exitosa, redirigir al usuario
        guardarDispositivo(response.data.dispositivo);
        localStorage.setItem("AuthToken", response.data.jwt_token);
        window.location.reload();
      } else {
        // Inicio de sesión fallido, mostrar mensaje de error
        setError(response.data.message);
      }
    } catch (error) {
      console.error('Error al iniciar sesión:', error);
      setError(error.response?.data?.message || t('auth.errorGenerico'));
      // Un código fallido ya no sirve: se vacía para teclear el siguiente
      if (mfaToken) setCodigoMfa('');
    } finally {
      setEnviando(false);
    }
  };

  const volverACredenciales = () => {
    setMfaToken('');
    setCodigoMfa('');
    setError('');
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      if (mfaToken && codigoMfa.length !== 6) return;
      handleSubmit();
    }
  };

  return (
    <main className='inicia-sesion'>
      <div className="bg-intro-blob bg-intro-blob-1"></div>
      <div className="inicio-idioma"><SelectorIdioma /></div>
      <Link to="/" className="volver-inicio"><FiArrowLeft /> {t('comun.volverInicio')}</Link>
      <div className='formulario-inicio'>
        <div className="logo-form-inicio">
          <img src="/images/logoadhoc.svg" alt={t('comun.marcaLogo')} />
          <p className="p-soporte">v3.2</p>
        </div>
        <h1>{t('auth.titulo')}</h1>
        <p>{t('auth.subtitulo')}</p>
        <div className='campos-inicio'>
          {!mfaToken && <div className="campo-inicio">
            <label htmlFor="user">{t('auth.usuarioOEmail')}</label>
            <div className="campo-inicio-input">
              <FiUser className="campo-inicio-icon" />
              <input autoComplete="off" type="text" id="user" value={usuario} onChange={handleUsuarioChange} placeholder={t('auth.placeholderUsuario')} />
            </div>
          </div>}
          {!mfaToken && <div className="campo-inicio">
            <label htmlFor='password'>{t('comun.contrasena')}</label>
            <div className="campo-inicio-input">
              <FiLock className="campo-inicio-icon" />
              <input autoComplete="off" type={mostrarContrasena ? 'text' : 'password'} name="password" value={contrasena} onChange={handleContrasenaChange} id="password" placeholder={t('auth.placeholderContrasena')} onKeyDown={handleKeyDown} />
              <button type="button" className="campo-inicio-toggle" onClick={() => setMostrarContrasena(prev => !prev)} aria-label={mostrarContrasena ? t('comun.ocultarContrasena') : t('comun.mostrarContrasena')}>
                {mostrarContrasena ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
          </div>}
          {mfaToken && <div className="campo-inicio campo-inicio-mfa">
            <label htmlFor="codigo-mfa-login">{t('auth.codigoMfa')}</label>
            <span className="campo-inicio-ayuda">{t('auth.ayudaMfa')}</span>
            <CodigoVerificacion
              id="codigo-mfa-login"
              anchoCompleto
              autoFocus
              value={codigoMfa}
              onChange={(codigo) => { setCodigoMfa(codigo); if (error) setError(''); }}
              onCompletar={(codigo) => handleSubmit(codigo)}
              onKeyDown={handleKeyDown}
              error={Boolean(error)}
            />
            {puedeRecordar && (
              <label className="casilla-confiar">
                <input type="checkbox" checked={confiar} onChange={(e) => setConfiar(e.target.checked)} />
                <span>{t('auth.confiarDispositivo', { dias: diasConfianza })}</span>
              </label>
            )}
            <button type="button" className="enlace-mfa" onClick={volverACredenciales}>
              <FiArrowLeft /> {t('auth.volverCredenciales')}
            </button>
          </div>}
          {!mfaToken && <p className='lost-password'>{t('auth.olvidasteContrasena')} <Link to={'/recuperar-contrasena'}>{t('auth.solicitaNueva')}</Link></p>}
          {sesionCaducada && (
            <div className="aviso-sesion" role="alert">
              {t('auth.sesionCaducada')}
            </div>
          )}
          {successMessage && <p className="success">{successMessage}</p>}
          {error && <p className="error">{error}</p>}
          <button type="button" onClick={() => handleSubmit()} disabled={enviando || (mfaToken && codigoMfa.length !== 6)}>{mfaToken ? t('auth.verificarMfa') : t('auth.acceder')}</button>
        </div>
      </div>
    </main>
  );
};

export default IniciarSesion;
